import React, { useState, useEffect } from 'react';
import { Plus, CreditCard as Edit, Trash2, Clock, User, Building2, CheckCircle, XCircle, Send, FileText } from 'lucide-react';
import InvoiceModal from './InvoiceModal';
import { collaboratorInvoicesService } from '../../services/supabaseService';
import { Timesheet } from '../../types/collaborators';
import { timesheetsService, collaboratorsService, projectsService } from '../../services/supabaseService';
import { notificationService } from '../../services/notificationService';
import TimesheetModal from './TimesheetModal';
import { usePermissions } from '../../hooks/usePermissions';

interface TimesheetsListProps {
  initialTimesheetId?: string | null;
  onTimesheetConsumed?: () => void;
}

const TimesheetsList: React.FC<TimesheetsListProps> = ({ initialTimesheetId, onTimesheetConsumed }) => {
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTimesheet, setEditingTimesheet] = useState<Timesheet | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'draft' | 'submitted' | 'approved' | 'rejected'>('all');

  // Modal de factura lanzado desde una fila de parte de horas.
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [invoicePreset, setInvoicePreset] = useState<any>(null);

  const { isAdmin, isCollaboradorProgramador } = usePermissions();
  const isProgrammerView = isCollaboradorProgramador();

  useEffect(() => {
    loadTimesheets();
  }, []);

  // Cuando venimos desde una notificación con un timesheet_id concreto,
  // abrimos el modal en modo edición en cuanto los datos estén cargados.
  useEffect(() => {
    if (!initialTimesheetId || timesheets.length === 0) return;
    const target = timesheets.find(t => t.id === initialTimesheetId);
    if (target) {
      setEditingTimesheet(target);
      setIsModalOpen(true);
      onTimesheetConsumed?.();
    }
  }, [initialTimesheetId, timesheets]);

  const loadTimesheets = async () => {
    try {
      setLoading(true);
      const timesheetsData = await timesheetsService.getAllWithDetails();
      setTimesheets(timesheetsData);
    } catch (error) {
      console.error('Error loading timesheets:', error);
      alert('Error al cargar los partes de horas');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTimesheet = () => {
    setEditingTimesheet(null);
    setIsModalOpen(true);
  };

  const handleEditTimesheet = (timesheet: Timesheet) => {
    setEditingTimesheet(timesheet);
    setIsModalOpen(true);
  };

  const handleSaveTimesheet = (timesheetData: Partial<Timesheet> & { tasks?: any[] }) => {
    if (editingTimesheet) {
      // Update existing
      updateTimesheet(timesheetData);
    } else {
      // Create new
      createTimesheet(timesheetData);
    }
  };

  const createTimesheet = async (timesheetData: Partial<Timesheet> & { tasks?: any[] }) => {
    try {
      const newTimesheet = await timesheetsService.create({
        userId: timesheetData.userId || timesheetData.collaboratorId,
        projectId: timesheetData.projectId,
        date: timesheetData.date,
        hours: timesheetData.hours,
        appliedRate: timesheetData.appliedRate,
        calculatedAmount: timesheetData.calculatedAmount,
        description: timesheetData.description,
        status: timesheetData.status || 'draft'
      });

      // Create tasks if provided
      if (timesheetData.tasks && timesheetData.tasks.length > 0 && newTimesheet.id) {
        const { supabase } = await import('../../services/supabaseService');

        const tasksToInsert = timesheetData.tasks.map((task, index) => ({
          timesheet_id: newTimesheet.id,
          title: task.title,
          description: task.description || '',
          hours: task.hours,
          order_index: index
        }));

        const { error: tasksError } = await supabase
          .from('timesheet_tasks')
          .insert(tasksToInsert);

        if (tasksError) {
          console.error('Error creating tasks:', tasksError);
          alert('⚠️ Parte de horas creado pero hubo un error al guardar las tareas');
        }
      }

      // Reload to get updated data with names
      await loadTimesheets();
      setIsModalOpen(false);
      setEditingTimesheet(null);
      alert('✅ Parte de horas creado correctamente');

      await notificationService.createNotification({
        code: 'TIMESHEET.CREATED',
        title: 'Parte de Horas Creado',
        body: `Nuevo parte de ${timesheetData.hours}h registrado`,
        severity: 'info',
        data: { timesheet_id: newTimesheet.id }
      });

      // La notificación in-app (arriba) es suficiente para admins.
      // El digest diario por email quedó retirado 2026-09-18 (queue nunca
      // se procesaba). Si se reactiva, mandar aquí sendImmediate en lugar
      // de enqueue.
    } catch (error) {
      console.error('Error creating timesheet:', error);
      alert('❌ Error al crear el parte de horas');
    }
  };

  const updateTimesheet = async (timesheetData: Partial<Timesheet> & { tasks?: any[] }) => {
    if (!editingTimesheet) return;

    try {
      await timesheetsService.update(editingTimesheet.id, {
        collaboratorId: timesheetData.collaboratorId,
        projectId: timesheetData.projectId,
        date: timesheetData.date,
        hours: timesheetData.hours,
        appliedRate: timesheetData.appliedRate,
        calculatedAmount: timesheetData.calculatedAmount,
        description: timesheetData.description,
        status: timesheetData.status,
        approvedBy: timesheetData.approvedBy,
        approvedAt: timesheetData.approvedAt
      });

      // Update tasks if provided
      if (timesheetData.tasks) {
        const { supabase } = await import('../../services/supabaseService');

        // Delete existing tasks
        await supabase
          .from('timesheet_tasks')
          .delete()
          .eq('timesheet_id', editingTimesheet.id);

        // Insert new tasks
        if (timesheetData.tasks.length > 0) {
          const tasksToInsert = timesheetData.tasks.map((task, index) => ({
            timesheet_id: editingTimesheet.id,
            title: task.title,
            description: task.description || '',
            hours: task.hours,
            order_index: index
          }));

          const { error: tasksError } = await supabase
            .from('timesheet_tasks')
            .insert(tasksToInsert);

          if (tasksError) {
            console.error('Error updating tasks:', tasksError);
            alert('⚠️ Parte de horas actualizado pero hubo un error al guardar las tareas');
          }
        }
      }

      await loadTimesheets();
      setIsModalOpen(false);
      setEditingTimesheet(null);
      alert('✅ Parte de horas actualizado correctamente');

      await notificationService.createNotification({
        code: 'TIMESHEET.UPDATED',
        title: 'Parte de Horas Actualizado',
        body: `Se actualizó el parte de ${timesheetData.hours}h`,
        severity: 'info',
        data: { timesheet_id: editingTimesheet.id }
      });
    } catch (error) {
      console.error('Error updating timesheet:', error);
      alert('❌ Error al actualizar el parte de horas');
    }
  };

  const handleApproveTimesheet = async (timesheetId: string) => {
    try {
      const { supabase } = await import('../../services/supabaseService');

      // Obtener datos del timesheet antes de aprobar
      const timesheet = timesheets.find(t => t.id === timesheetId);
      if (!timesheet) {
        throw new Error('Timesheet no encontrado');
      }

      // Obtener nombre del usuario actual
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      const { data: currentUserData } = await supabase
        .from('users')
        .select('name')
        .eq('id', currentUser?.id)
        .maybeSingle();

      const approverName = currentUserData?.name || 'Administrador';

      await timesheetsService.update(timesheetId, {
        status: 'approved',
        approvedBy: approverName,
        approvedAt: new Date()
      });

      await loadTimesheets();
      alert('✅ Parte de horas aprobado');

      // Targeteada al colaborador. Antes iba broadcast (user_id=null) y no
      // le aparecía nítidamente. Ahora salta en su campana con deep_link
      // a Plantilla > Facturas para que pueda subir su factura.
      await notificationService.createNotification({
        user_id: timesheet.userId,
        code: 'TIMESHEET.APPROVED',
        title: 'Parte de horas aprobado',
        body: `${timesheet.hours}h en ${timesheet.projectName} · ${timesheet.calculatedAmount}€`,
        severity: 'info',
        data: { timesheet_id: timesheetId, project_name: timesheet.projectName },
      });

      // Enviar email inmediato al colaborador
      try {
        const { emailNotificationService } = await import('../../services/emailNotificationService');

        // Validar que existe userId
        if (!timesheet.userId) {
          console.error('❌ Error: timesheet no tiene userId válido', timesheet);
          throw new Error('El parte de horas no tiene un usuario asignado');
        }

        // Obtener información del colaborador
        const { data: user, error: userError } = await supabase
          .from('users')
          .select('id, name, email')
          .eq('id', timesheet.userId)
          .maybeSingle();

        if (userError) {
          console.error('❌ Error obteniendo usuario:', userError);
          throw userError;
        }

        if (user) {
          await emailNotificationService.sendTimesheetApprovedEmail(
            user.name,
            user.email,
            user.id,
            timesheet.projectName,
            timesheet.hours,
            timesheet.calculatedAmount,
            timesheet.date,
            approverName,
            timesheetId
          );
          console.log('✅ Email de aprobación enviado a:', user.email);
        } else {
          console.error('❌ Usuario no encontrado con ID:', timesheet.userId);
        }
      } catch (emailError) {
        console.error('❌ Error enviando email de aprobación:', emailError);
      }
    } catch (error) {
      console.error('Error approving timesheet:', error);
      alert('❌ Error al aprobar el parte de horas');
    }
  };

  const handleRejectTimesheet = async (timesheetId: string) => {
    const reason = window.prompt('Motivo del rechazo (opcional):');
    try {
      const { supabase } = await import('../../services/supabaseService');

      // Obtener datos del timesheet antes de rechazar
      const timesheet = timesheets.find(t => t.id === timesheetId);
      if (!timesheet) {
        throw new Error('Timesheet no encontrado');
      }

      await timesheetsService.update(timesheetId, {
        status: 'rejected'
      });

      await loadTimesheets();
      alert('✅ Parte de horas rechazado');

      await notificationService.createNotification({
        code: 'TIMESHEET.REJECTED',
        title: 'Parte de Horas Rechazado',
        body: 'Un parte de horas ha sido rechazado',
        severity: 'warn',
        data: { timesheet_id: timesheetId, reason }
      });

      // Enviar email inmediato al colaborador
      try {
        const { emailNotificationService } = await import('../../services/emailNotificationService');

        // Validar que existe userId
        if (!timesheet.userId) {
          console.error('❌ Error: timesheet no tiene userId válido', timesheet);
          throw new Error('El parte de horas no tiene un usuario asignado');
        }

        // Obtener información del colaborador
        const { data: user, error: userError } = await supabase
          .from('users')
          .select('id, name, email')
          .eq('id', timesheet.userId)
          .maybeSingle();

        if (userError) {
          console.error('❌ Error obteniendo usuario:', userError);
          throw userError;
        }

        if (user) {
          await emailNotificationService.sendTimesheetRejectedEmail(
            user.name,
            user.email,
            user.id,
            timesheet.projectName,
            timesheet.hours,
            timesheet.date,
            reason || 'No se especificó un motivo',
            timesheetId
          );
          console.log('✅ Email de rechazo enviado a:', user.email);
        } else {
          console.error('❌ Usuario no encontrado con ID:', timesheet.userId);
        }
      } catch (emailError) {
        console.error('❌ Error enviando email de rechazo:', emailError);
      }
    } catch (error) {
      console.error('Error rejecting timesheet:', error);
      alert('❌ Error al rechazar el parte de horas');
    }
  };

  const handleDeleteTimesheet = async (timesheetId: string) => {
    if (window.confirm('¿Eliminar este parte de horas? Esta acción no se puede deshacer.')) {
      try {
        await timesheetsService.delete(timesheetId);
        await loadTimesheets();
        alert('✅ Parte de horas eliminado');

        await notificationService.createNotification({
          code: 'TIMESHEET.DELETED',
          title: 'Parte de Horas Eliminado',
          body: 'Un parte de horas ha sido eliminado',
          severity: 'warn',
          data: { timesheet_id: timesheetId }
        });
      } catch (error) {
        console.error('Error deleting timesheet:', error);
        alert('❌ Error al eliminar el parte de horas');
      }
    }
  };

  const getStatusColor = (status: Timesheet['status']) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'submitted': return 'bg-blue-100 text-blue-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: Timesheet['status']) => {
    switch (status) {
      case 'draft': return 'Borrador';
      case 'submitted': return 'Enviado';
      case 'approved': return 'Aprobado';
      case 'rejected': return 'Rechazado';
      default: return status;
    }
  };

  const filteredTimesheets = filter === 'all' 
    ? timesheets 
    : timesheets.filter(t => t.status === filter);

  const totalHours = filteredTimesheets.reduce((sum, t) => sum + t.hours, 0);
  const totalAmount = filteredTimesheets.reduce((sum, t) => sum + t.calculatedAmount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3">Cargando partes de horas...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Partes de Horas</h2>
          <p className="text-gray-600">Registro y aprobación de horas trabajadas por colaboradores</p>
        </div>
        <button
          onClick={handleCreateTimesheet}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Nuevo Parte</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">Filtrar por estado:</span>
          <div className="flex space-x-2">
            {[
              { value: 'all', label: 'Todos' },
              { value: 'draft', label: 'Borradores' },
              { value: 'submitted', label: 'Enviados' },
              { value: 'approved', label: 'Aprobados' },
              { value: 'rejected', label: 'Rechazados' }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => setFilter(option.value as any)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  filter === option.value
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">Total Horas</p>
              <p className="text-2xl font-bold text-blue-900">{totalHours}h</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Clock className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">Importe Total</p>
              <p className="text-2xl font-bold text-green-900">{totalAmount.toLocaleString()}€</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-yellow-600">Pendientes</p>
              <p className="text-2xl font-bold text-yellow-900">
                {timesheets.filter(t => t.status === 'submitted').length}
              </p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Send className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-purple-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600">Aprobados</p>
              <p className="text-2xl font-bold text-purple-900">
                {timesheets.filter(t => t.status === 'approved').length}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Timesheets Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Colaborador
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Proyecto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Horas
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Importe
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTimesheets.map((timesheet) => (
                <tr
                  key={timesheet.id}
                  className="hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => handleEditTimesheet(timesheet)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">
                        {timesheet.collaboratorName}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <Building2 className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900">{timesheet.projectName}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {(timesheet.date instanceof Date ? timesheet.date : new Date(timesheet.date)).toLocaleDateString('es-ES')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">
                        {timesheet.hours}h
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">
                      {timesheet.calculatedAmount.toLocaleString()}€
                    </span>
                    <div className="text-xs text-gray-500">
                      {timesheet.appliedRate}€/h
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(timesheet.status)}`}>
                      {getStatusLabel(timesheet.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      {timesheet.status === 'submitted' && !isProgrammerView && (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleApproveTimesheet(timesheet.id); }}
                            className="text-green-600 hover:text-green-900 p-1 rounded"
                            title="Aprobar"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRejectTimesheet(timesheet.id); }}
                            className="text-red-600 hover:text-red-900 p-1 rounded"
                            title="Rechazar"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {timesheet.status === 'approved' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Pre-cargamos el modal con este parte para que
                            // el colaborador cree su factura directamente.
                            setInvoicePreset({
                              userId: timesheet.userId,
                              collaboratorId: timesheet.userId,
                              collaboratorName: timesheet.userName,
                              date: new Date(),
                              amount: timesheet.calculatedAmount,
                              currency: 'EUR',
                              status: 'received',
                              timesheetIds: [timesheet.id],
                            });
                            setInvoiceModalOpen(true);
                          }}
                          className="text-emerald-600 hover:text-emerald-900 p-1 rounded"
                          title="Crear factura de este parte"
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEditTimesheet(timesheet); }}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteTimesheet(timesheet.id); }}
                        className="text-red-600 hover:text-red-900 p-1 rounded"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredTimesheets.length === 0 && (
          <div className="text-center py-12">
            <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay partes de horas</h3>
            <p className="text-gray-500 mb-6">
              {filter === 'all' 
                ? 'Comienza registrando el primer parte de horas'
                : `No hay partes con estado "${getStatusLabel(filter as any)}"`
              }
            </p>
            {filter === 'all' && (
              <button
                onClick={handleCreateTimesheet}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mx-auto"
              >
                <Plus className="w-4 h-4" />
                <span>Nuevo Parte</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modal parte de horas */}
      <TimesheetModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingTimesheet(null);
        }}
        onSave={handleSaveTimesheet}
        timesheet={editingTimesheet}
      />

      {/* Modal factura lanzado desde un parte aprobado */}
      <InvoiceModal
        isOpen={invoiceModalOpen}
        onClose={() => {
          setInvoiceModalOpen(false);
          setInvoicePreset(null);
        }}
        invoice={invoicePreset}
        onSave={async (invoiceData: any) => {
          try {
            // Normalizamos la fecha: el preset la pasa como Date pero
            // el modal la convierte a string 'YYYY-MM-DD' al editar.
            const rawDate = invoiceData.date;
            const dateObj = rawDate instanceof Date
              ? rawDate
              : (typeof rawDate === 'string' ? new Date(rawDate + 'T00:00:00') : new Date());

            const created = await collaboratorInvoicesService.create({
              userId: invoiceData.collaboratorId || invoiceData.userId,
              invoiceNumber: invoiceData.invoiceNumber,
              date: dateObj,
              amount: Number(invoiceData.amount || 0),
              currency: invoiceData.currency || 'EUR',
              attachmentUrl: invoiceData.attachmentUrl,
              attachmentName: invoiceData.attachmentName,
              status: 'received',
              timesheetIds: invoiceData.timesheetIds || [],
            });

            // Aviso a admin + email (mismo camino que InvoicesList).
            try {
              const { notificationService } = await import('../../services/notificationService');
              const collabName = invoiceData.collaboratorName || 'El colaborador';
              await notificationService.createNotification({
                code: 'INVOICE.CREATED',
                title: 'Factura recibida',
                body: `${collabName} ha enviado la factura ${invoiceData.invoiceNumber} · ${Number(invoiceData.amount || 0)} ${invoiceData.currency || 'EUR'}`,
                severity: 'info',
                data: {
                  invoice_id: created.id,
                  invoice_number: invoiceData.invoiceNumber,
                  collaborator_name: collabName,
                },
              });
            } catch (notifErr) {
              console.warn('No se pudo crear notificación de factura:', notifErr);
            }

            setInvoiceModalOpen(false);
            setInvoicePreset(null);
            alert('✅ Factura enviada al administrador');
          } catch (err: any) {
            console.error('Error creando factura desde parte:', err);
            alert('Error creando factura: ' + (err?.message || err));
          }
        }}
      />
    </div>
  );
};

export default TimesheetsList;